/* eslint-disable react/forbid-prop-types */
import PropTypes from "prop-types";

import {
  LumenResultScore,
  LUMEN_SECTION_TYPE,
  LUMEN_SUMMARY_MESSAGE_TYPE,
} from "manager/config/lumen.config";
import { SecurityDepositDeductionStatus } from "manager/interfaces/types";
import {
  APPLICANT_TYPES,
  EMPLOYMENT_STATUSES,
  INCOME_ASSET_STATUS_CODES,
  VERIFICATION_STATUSES,
} from "shared/config/constants";
import {
  countyCriminalRecordsPropType,
  countyCriminalSearchStatusPropType,
  actionFlagPropType,
  keyPropType,
  dealUiStatusPropType,
} from "shared/utils/propTypes";

export const sourcePropType = PropTypes.shape({
  id: keyPropType.isRequired,
  value: PropTypes.string.isRequired,
});

export const dropdownPropertiesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  })
);

export const dropdownLeaseStatusesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })
);

export const lumenMessagesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    type: PropTypes.oneOf(Object.values(LUMEN_SUMMARY_MESSAGE_TYPE)).isRequired,
    text: PropTypes.string.isRequired,
  })
);

export const lumenScorePropType = PropTypes.oneOf(
  Object.values(LumenResultScore)
);

export const lumenApplicantResultPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  type: PropTypes.oneOf(Object.values(APPLICANT_TYPES)).isRequired,
  score: PropTypes.oneOf(Object.values(LumenResultScore)).isRequired,
});

export const lumenResultPropType = PropTypes.shape({
  dealId: PropTypes.number.isRequired,
  score: lumenScorePropType.isRequired,
  messages: lumenMessagesPropType.isRequired,
  applicants: PropTypes.arrayOf(lumenApplicantResultPropType).isRequired,
});

export const lumenSectionTypePropType = PropTypes.oneOf(
  Object.values(LUMEN_SECTION_TYPE)
);

export const dealDetailsPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  dealUiStatus: dealUiStatusPropType.isRequired,
  dealUpdatedOn: PropTypes.string,
  dealUpdatedBy: PropTypes.string,
  lease: PropTypes.object,
  leaseStatus: PropTypes.string,
  applicantsSummary: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      isSubmitted: PropTypes.bool.isRequired,
      applicantType: PropTypes.string.isRequired,
    })
  ).isRequired,
  propertyHasDocuments: PropTypes.bool,
  employee: PropTypes.shape({
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
  unit: PropTypes.shape({
    id: keyPropType,
    name: PropTypes.string,
    status: PropTypes.string,
    yardiConnected: PropTypes.bool.isRequired,
  }),
  rent: PropTypes.number,
  securityDeposit: PropTypes.number,
  totalAnnualIncome: PropTypes.number,
  totalAnnualIncomeIsEdited: PropTypes.bool.isRequired,
  incomeRentMultiple: PropTypes.number,
  totalAssets: PropTypes.number,
  totalAssetsIsEdited: PropTypes.bool.isRequired,
  averageCreditScore: PropTypes.number,
  criminalRecords: PropTypes.bool,
  hideCriminalHistory: PropTypes.bool.isRequired,
  evictions: PropTypes.bool.isRequired,
  isInNoEvictionState: PropTypes.bool.isRequired,
  isInStateAllowingCreditScore: PropTypes.bool.isRequired,
  canRunCountyCriminalSearch: PropTypes.bool.isRequired,
  countyCriminalSearchStatus: PropTypes.shape({
    value: countyCriminalSearchStatusPropType.isRequired,
    helpText: PropTypes.string,
  }),
  countyCriminalSearchStartedAt: PropTypes.string,
  countyCriminalSearchCompletedAt: PropTypes.string,
  countyCriminalSearchRequestedByName: PropTypes.string,
  lumenEnabled: PropTypes.bool.isRequired,
  requiresNewUnit: PropTypes.bool.isRequired,
  canBeApproved: actionFlagPropType.isRequired,
  canBeConditionallyApproved: actionFlagPropType.isRequired,
  canBeConditionallyProceeded: actionFlagPropType.isRequired,
  canBeDeclined: actionFlagPropType.isRequired,
  canBeCanceled: actionFlagPropType.isRequired,
  canEditRent: actionFlagPropType.isRequired,
  hasConditionallyProceedEnabled: PropTypes.bool.isRequired,
  isConditionallyProceeded: PropTypes.bool.isRequired,
  canCreateLease: actionFlagPropType.isRequired,
  blockDealSubmitIfIncomeDataIsMissing: PropTypes.bool.isRequired,
  extraMessagesForUi: PropTypes.arrayOf(PropTypes.string),
});

export const applicantPropType = PropTypes.shape({
  id: keyPropType.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  incomeRentMultiple: PropTypes.number,
  creditScore: PropTypes.number,
  criminalRecords: PropTypes.bool,
  evictions: PropTypes.bool,
  applicantType: PropTypes.string,
  income: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(VERIFICATION_STATUSES)).isRequired,
    value: PropTypes.number,
  }),
  assets: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(VERIFICATION_STATUSES)).isRequired,
    value: PropTypes.number,
  }),
  skippedIncome: PropTypes.bool,
  skippedAsset: PropTypes.bool,
  skippedBgcc: PropTypes.bool,
  hasFeeToPay: PropTypes.bool,
  hasApplicationFee: PropTypes.bool,
  hasHoldingDeposit: PropTypes.bool,
  isApplicationFeeUnpaid: PropTypes.bool,
  isHoldingDepositUnpaid: PropTypes.bool,
  percentIncomeVerified: PropTypes.number,
  isSubmitted: PropTypes.bool,
  isPersonaEnabled: PropTypes.bool,
  applicationLink: PropTypes.string,
});

export const propertySettingsPropTypes = PropTypes.shape({
  id: keyPropType,
  portfolioName: PropTypes.string,
  portfolioIsDefault: PropTypes.bool,
  isDealSetupFromPortfolio: PropTypes.bool,
  dealSetupId: PropTypes.number,
  yardiConnected: PropTypes.bool,
  yardiPropertyId: keyPropType,
  deleted: PropTypes.bool,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  name: PropTypes.string,
  propertyType: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zipcode: PropTypes.string,
  usePropertyBranding: PropTypes.bool,
  logo: PropTypes.string,
  fqlVrn: PropTypes.string,
  thumbnailLogo: PropTypes.string,
  isPublic: PropTypes.bool,
  externalReference: PropTypes.string,
  displayOnPortal: PropTypes.bool,
  notificationEmail: PropTypes.string,
  hideCriminalHistory: PropTypes.bool,
  portfolio: PropTypes.number,
});

export const unitPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  bedrooms: PropTypes.number,
  unitType: PropTypes.string,
  updatedAt: PropTypes.string,
  squareFeet: PropTypes.number,
  bathrooms: PropTypes.string,
  propertyId: PropTypes.string.isRequired,
  propertyName: PropTypes.string.isRequired,
  pmsConnected: PropTypes.bool,
  provider: PropTypes.string,
  fqlVrn: PropTypes.string,
  yardiConnected: PropTypes.bool.isRequired,
  zombieUnit: PropTypes.bool.isRequired,
  unitListingCurrent: PropTypes.shape({
    unitAvailableOn: PropTypes.string.isRequired,
    rent: PropTypes.number.isRequired,
    securityDeposit: PropTypes.number,
    note: PropTypes.string,
  }),
});

// NOTE: auto-generated prop-types
export const applicationSummaryPropType = PropTypes.shape({
  id: keyPropType,
  isSubmitted: PropTypes.bool,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  leaseSigned: PropTypes.bool,
  email: PropTypes.string,
  applicantType: PropTypes.string,
  applicationLink: PropTypes.string,
  hasAutoLinkedEmployment: PropTypes.bool,
  isPrimary: PropTypes.bool,
  isPersonaEnabled: PropTypes.bool,
  isArgyleEnabled: PropTypes.bool,
  yardiId: keyPropType,
  creditScore: PropTypes.number,
  isCountyCriminalSearchEligible: PropTypes.bool,
  income: PropTypes.shape({
    value: keyPropType,
    status: PropTypes.string,
  }),
  assets: PropTypes.shape({
    value: keyPropType,
    status: PropTypes.string,
  }),
  incomeRentMultiple: PropTypes.number,
  criminalRecords: PropTypes.bool,
  evictions: PropTypes.bool,
  incomeNotes: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.number,
    })
  ),
  phoneNumber: PropTypes.string,
  birthdate: PropTypes.string,
  preferredLeaseDurationMonths: PropTypes.string,
  preferredLeaseStartDate: PropTypes.string,
  skippedAsset: PropTypes.bool,
  skippedIncome: PropTypes.bool,
  skippedBgcc: PropTypes.bool,
  percentIncomeVerified: PropTypes.number,
  requiresResidentialHistoryDuration: PropTypes.bool,
  residentialHistoryRange: PropTypes.string,
  residentialHistoryDuration: PropTypes.number,
  requiresEmploymentHistoryDuration: PropTypes.bool,
  employmentHistoryRange: PropTypes.string,
  employmentHistoryDuration: PropTypes.number,
  employmentNonMatchingCriteriaNotes: PropTypes.string,
  residentialNonMatchingCriteriaNotes: PropTypes.string,
  residentialDurationValid: PropTypes.bool,
  employmentDurationValid: PropTypes.bool,
  employmentStatus: PropTypes.oneOf(Object.values(EMPLOYMENT_STATUSES)),
  assetSkippedReason: PropTypes.string,
  assetBankName: PropTypes.string,
  ssn: PropTypes.string,
  canShowSsn: PropTypes.bool,
  countyCriminalSearchRequestedByName: PropTypes.string,
  deleted: PropTypes.bool,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  invitedDate: PropTypes.string,
  whoInviteDate: PropTypes.string,
  relationship: PropTypes.string,
  status: PropTypes.number,
  submittedAt: PropTypes.string,
  withdrawnAt: PropTypes.string,
  startedAt: PropTypes.string,
  dealAssociatedAt: PropTypes.string,
  screeningCriteriaDocumentAgreedAt: PropTypes.string,
  linkEmploymentFinished: PropTypes.bool,
  personaInquiryId: PropTypes.string,
  personaNumberOfAttempts: PropTypes.number,
  personaStatus: PropTypes.string,
  personaSystemError: PropTypes.bool,
  personaInquiryStatus: PropTypes.string,
  countyCriminalSearchRequestedAt: PropTypes.string,
  countyCriminalSearchCompletedAt: PropTypes.string,
  countyCriminalSearchStatus: PropTypes.string,
  applicant: PropTypes.number,
  invitedBy: PropTypes.number,
  deal: PropTypes.number,
  pdfLight: PropTypes.string,
  dealAssociatedBy: PropTypes.number,
  countyCriminalSearchRequestedBy: PropTypes.string,
});

export const incomeAssetsAccountsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    accountType: PropTypes.string,
    currentBalance: PropTypes.number,
    availableBalance: PropTypes.number,
    isIncluded: PropTypes.bool,
  })
);

export const incomeAssetsIncomesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    frequency: PropTypes.string,
    maxIncome: PropTypes.number,
    meanIncome: PropTypes.number,
    status: PropTypes.string,
    lastPayment: PropTypes.string,
    name: PropTypes.string,
    isIncluded: PropTypes.bool,
    id: PropTypes.number,
  })
);

export const incomeAssetsSummaryPropType = PropTypes.shape({
  accounts: incomeAssetsAccountsPropType,
  incomes: incomeAssetsIncomesPropType,
  percentIncomeVerified: PropTypes.number,
  totalIncome: PropTypes.number,
  isInTheGreen: PropTypes.bool,
  averageMonthlyIncome: PropTypes.string,
  numberOfActiveIncomeStreams: PropTypes.number,
  statedEmployer: PropTypes.arrayOf(PropTypes.string),
  notes: PropTypes.string,
  assetsStatus: PropTypes.oneOf(Object.values(INCOME_ASSET_STATUS_CODES)),
  totalAssets: PropTypes.number,
});

export const skippedIncomeAssetsPropType = PropTypes.shape({
  message: PropTypes.string,
  label: PropTypes.string,
});

export const idVerificationPropType = PropTypes.shape({
  verificationType: PropTypes.string,
  status: PropTypes.string,
  verificationDate: PropTypes.string,
  details: PropTypes.shape({
    id: PropTypes.number,
    verificationFiles: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        url: PropTypes.string,
      })
    ),
    checks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        status: PropTypes.string,
        reasons: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    age: PropTypes.number,
    completedAt: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    inquiryId: PropTypes.string,
    status: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
    identificationNumber: PropTypes.string,
    addressStreet: PropTypes.string,
    addressSecondLine: PropTypes.any,
    addressCity: PropTypes.string,
    addressSubdivision: PropTypes.string,
    addressSubdivisionAbbr: PropTypes.string,
    addressPostalCode: PropTypes.string,
    addressPostalCodeAbbr: PropTypes.string,
    addressCountryCode: PropTypes.string,
    expirationDate: PropTypes.string,
    phoneNumber: PropTypes.any,
    documentClass: PropTypes.string,
    lastSyncDate: PropTypes.string,
    application: PropTypes.number,
  }),
});

export const fullApplicationPropType = PropTypes.shape({
  id: keyPropType,
  applicationSummary: applicationSummaryPropType,
  otherApplicants: PropTypes.array,
  emergencyContacts: PropTypes.array,
  residentialHistories: PropTypes.array,
  employmentHistories: PropTypes.array,
  additionalInformation: PropTypes.shape({
    id: PropTypes.number,
    pets: PropTypes.array,
    vehicles: PropTypes.array,
    answers: PropTypes.array,
    additionalQuestions: PropTypes.array,
    deleted: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    smokes: PropTypes.bool,
    industry: PropTypes.string,
    windowGuard: PropTypes.string,
    kid: PropTypes.string,
    knobRider: PropTypes.string,
    petComments: PropTypes.string,
    application: PropTypes.number,
    marketingSource: keyPropType,
  }),
  incomeAsset: incomeAssetsSummaryPropType,
  backgroundCreditCheck: PropTypes.shape({
    creditScoreSummary: PropTypes.shape({
      score: PropTypes.number,
      creditLimit: PropTypes.number,
      creditBalance: PropTypes.number,
      firstName: PropTypes.string,
      middleName: PropTypes.string,
      lastName: PropTypes.string,
      akas: PropTypes.array,
      addresses: PropTypes.arrayOf(
        PropTypes.shape({
          address: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          zipcode: PropTypes.string,
          dateReported: PropTypes.string,
          id: keyPropType,
        })
      ),
      scoreFactors: PropTypes.arrayOf(PropTypes.string),
      fraudIndicators: PropTypes.arrayOf(PropTypes.string),
      accounts: PropTypes.any,
    }),
    criminalRecords: PropTypes.shape({}),
    evictionRecords: PropTypes.shape({
      states: PropTypes.number,
      totalEvictions: PropTypes.number,
      evictionRecordGroup: PropTypes.array,
    }),
    reportStatus: PropTypes.string,
    backgroundAndCreditCheckId: PropTypes.number,
    isComplete: PropTypes.bool,
  }),
  unitName: PropTypes.string,
  property: PropTypes.string,
  requiredAttachments: PropTypes.array,
  otherAttachments: PropTypes.array,
  requiredAdditionalInformation: PropTypes.shape({
    pets: PropTypes.bool,
    smokes: PropTypes.bool,
    vehicles: PropTypes.bool,
    industry: PropTypes.bool,
    windowGuard: PropTypes.bool,
    knobRider: PropTypes.bool,
    kid: PropTypes.bool,
    marketingSource: PropTypes.bool,
  }),
  isInNoEvictionState: PropTypes.bool,
  skippedAsset: PropTypes.bool,
  skippedIncome: PropTypes.bool,
  skippedBgcc: PropTypes.bool,
  percentIncomeVerified: PropTypes.number,
  screeningCriteriaDocumentUrl: PropTypes.string,
  screeningCriteriaDocumentAgreedAt: PropTypes.string,
  preventEditApplicationAfterSubmit: PropTypes.bool,
  hideCriminalHistory: PropTypes.bool,
  hasConditionallyProceedEnabled: PropTypes.bool.isRequired,
  isConditionallyProceeded: PropTypes.bool.isRequired,
  countyCriminalRecords: countyCriminalRecordsPropType,
  hideOccupantsButAllowDependents: PropTypes.bool,
  idVerification: idVerificationPropType,
  deleted: PropTypes.bool,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  applicantType: PropTypes.number,
  invitedDate: PropTypes.string,
  whoInviteDate: PropTypes.string,
  relationship: PropTypes.string,
  status: PropTypes.number,
  submittedAt: PropTypes.string,
  withdrawnAt: PropTypes.string,
  startedAt: PropTypes.string,
  dealAssociatedAt: PropTypes.string,
  linkEmploymentFinished: PropTypes.bool,
  employmentNonMatchingCriteriaNotes: PropTypes.string,
  residentialNonMatchingCriteriaNotes: PropTypes.string,
  residentialDurationValid: PropTypes.bool,
  employmentDurationValid: PropTypes.bool,
  personaInquiryId: PropTypes.string,
  personaNumberOfAttempts: PropTypes.number,
  personaStatus: PropTypes.string,
  personaSystemError: PropTypes.bool,
  personaInquiryStatus: PropTypes.string,
  countyCriminalSearchRequestedAt: PropTypes.string,
  countyCriminalSearchCompletedAt: PropTypes.string,
  countyCriminalSearchAppRef: PropTypes.string,
  countyCriminalSearchStatus: PropTypes.string,
  applicant: PropTypes.number,
  invitedBy: PropTypes.number,
  deal: PropTypes.number,
  pdfLight: PropTypes.string,
  dealAssociatedBy: PropTypes.number,
  countyCriminalSearchRequestedBy: PropTypes.string,
});

export const dealSetupPropType = PropTypes.shape({
  id: PropTypes.number,
  additionalQuestions: PropTypes.array,
  requiredAttachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      attachment: PropTypes.any,
      deleted: PropTypes.any,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      isActive: PropTypes.bool,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
      applicantType: PropTypes.number,
      attachmentType: PropTypes.string,
      deal: PropTypes.any,
      dealSetup: PropTypes.number,
      conditionalRequest: PropTypes.any,
    })
  ),
  contentObject: PropTypes.any,
  deleted: PropTypes.any,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  requiresIncomeAssetApplicantOccupant: PropTypes.bool,
  requiresIncomeAssetGuarantors: PropTypes.bool,
  requiresAssetVerifLinkedEmploymentApplicantOccupant: PropTypes.bool,
  requiresAssetVerifLinkedEmploymentGuarantors: PropTypes.bool,
  requiresBackgroundCreditCheckApplicantOccupant: PropTypes.bool,
  requiresBackgroundCreditCheckGuarantors: PropTypes.bool,
  allowManagerViewSsnApplicantOccupant: PropTypes.bool,
  allowManagerViewSsnGuarantor: PropTypes.bool,
  requiresRenterProfileApplicantOccupant: PropTypes.bool,
  requiresRenterProfileGuarantor: PropTypes.bool,
  requiresLeasesPreferencesApplicant: PropTypes.bool,
  requiresReferencesApplicant: PropTypes.bool,
  applicantApplicationFee: PropTypes.number,
  guarantorApplicationFee: PropTypes.number,
  occupantOver18ApplicationFee: PropTypes.number,
  incomeAndAssetVerificationFee: PropTypes.number,
  backgroundAndCreditChecksFee: PropTypes.number,
  applicantPaysProcessingFee: PropTypes.bool,
  requiresResidentialHistoryDurationApplicantOccupant: PropTypes.bool,
  residentialHistoryRangeApplicantOccupant: PropTypes.any,
  residentialHistoryDurationApplicantOccupant: PropTypes.any,
  requiresResidentialHistoryDurationGuarantor: PropTypes.bool,
  residentialHistoryRangeGuarantor: PropTypes.any,
  residentialHistoryDurationGuarantor: PropTypes.any,
  requiresEmploymentHistoryDurationApplicantOccupant: PropTypes.bool,
  employmentHistoryRangeApplicantOccupant: PropTypes.any,
  employmentHistoryDurationApplicantOccupant: PropTypes.any,
  requiresEmploymentHistoryDurationGuarantor: PropTypes.bool,
  employmentHistoryRangeGuarantor: PropTypes.any,
  employmentHistoryDurationGuarantor: PropTypes.any,
  requiresApplicationQuestionsApplicant: PropTypes.bool,
  requiresApplicationQuestionsGuarantor: PropTypes.bool,
  requiresApplicationQuestionsOccupant: PropTypes.bool,
  requiresApplicantAttachements: PropTypes.bool,
  requiresGuarantorAttachements: PropTypes.bool,
  requiresOccupantAttachements: PropTypes.bool,
  requiresApplicantAdditionalAttachementsForIncome: PropTypes.bool,
  requiresGuarantorAdditionalAttachementsForIncome: PropTypes.bool,
  requiresOccupantAdditionalAttachementsForIncome: PropTypes.bool,
  requiresApplicantAdditionalAttachementsForBackground: PropTypes.bool,
  requiresGuarantorAdditionalAttachementsForBackground: PropTypes.bool,
  requiresOccupantAdditionalAttachementsForBackground: PropTypes.bool,
  requiresApplicantPetInformation: PropTypes.bool,
  requiresApplicantSmokeInformation: PropTypes.bool,
  requiresApplicantVehicleInformation: PropTypes.bool,
  requiresApplicantMarketingSource: PropTypes.bool,
  requiresApplicantIndustry: PropTypes.bool,
  requiresApplicantWindowGuard: PropTypes.bool,
  requiresApplicantKnobRider: PropTypes.bool,
  requiresApplicantLeadPaint: PropTypes.bool,
  requiresApplicantKids: PropTypes.bool,
  requiresApplicantOccupantPersonaIdentification: PropTypes.bool,
  requiresGuarantorPersonaIdentification: PropTypes.bool,
  requiresApplicantOccupantArgyleVerification: PropTypes.bool,
  requiresGuarantorArgyleVerification: PropTypes.bool,
  countyCriminalSearchApplicantOccupantSync: PropTypes.string,
  usesConditionallyProceed: PropTypes.bool,
  countyCriminalSearchGuarantorsSync: PropTypes.string,
  objectId: PropTypes.any,
  contentType: PropTypes.any,
});

export const activeCompanyPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
});

export const unblockApplicationItemPropType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  showAction: PropTypes.bool.isRequired,
  action: PropTypes.func.isRequired,
});

export const SecurityDepositStatusPropType = PropTypes.oneOf(
  Object.values(SecurityDepositDeductionStatus)
);
